import { Button, Stack, TextField } from "@mui/material";
import { useEffect, useState } from "react";
import { ApiError, type Branding, branding, resetPassword } from "../../data/api";
import { bareMessage } from "../../data/data";
import { Failure, Field } from "../../ui/components/primitives";
import { LandingLayout } from "./LandingLayout";

// ---
// Password reset
//
// Reached from the link in a reset email: the auth server verifies the token, then redirects here
// with it as `?token=`. Like an invitation, the token is single-use and never stored. An invalid
// or expired link arrives as `?error=INVALID_TOKEN` instead of a token.
// ---

const MIN_LENGTH = 12;

export function Reset({
  token,
  error: linkError,
  tenant,
  onDone,
}: {
  token: string | null;
  error: string | null;
  /**
   * Whose words this reset was sent in, carried on the link.
   *
   * The token says nothing about a tenant, so the server puts it here when it resolves one for
   * the mail — which keeps the page and the message that led to it looking like each other.
   */
  tenant?: string | null;
  onDone: () => void;
}): React.JSX.Element {
  const [brand, setBrand] = useState<Branding | null>(null);
  useEffect(() => {
    if (tenant == null || tenant === "") return;
    void branding
      .read(tenant)
      .then(setBrand)
      .catch(() => undefined);
  }, [tenant]);

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const mismatch = confirm !== "" && confirm !== password;
  const tooShort = password !== "" && password.length < MIN_LENGTH;

  if (token === null || linkError !== null) {
    return (
      <LandingLayout
        brand={brand}
        title="Link unavailable"
        subtitle="This reset link has expired, was already used, or is not valid. Request a new one from the sign-in page."
      >
        <Button variant="outlined" size="large" fullWidth onClick={onDone}>
          Go to sign in
        </Button>
      </LandingLayout>
    );
  }

  if (done) {
    return (
      <LandingLayout
        brand={brand}
        title="Password changed"
        subtitle="Sign in with your new password. Other sessions have been signed out."
      >
        <Button variant="contained" size="large" fullWidth onClick={onDone}>
          Go to sign in
        </Button>
      </LandingLayout>
    );
  }

  return (
    <LandingLayout brand={brand} title="Choose a new password" subtitle="This link works once.">
      <form
        onSubmit={(event) => {
          event.preventDefault();
          setBusy(true);
          setError(null);
          void resetPassword(token, password)
            .then(() => setDone(true))
            .catch((caught: unknown) => setError(bareMessage(caught)))
            .finally(() => setBusy(false));
        }}
      >
        <Stack spacing={4}>
          <Field label="New password" error={tooShort} hint={`At least ${MIN_LENGTH} characters`}>
            <TextField
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              required
              fullWidth
              autoFocus
              autoComplete="new-password"
              error={tooShort}
            />
          </Field>
          <Field label="Confirm" error={mismatch} hint={mismatch ? "Passwords do not match" : " "}>
            <TextField
              type="password"
              value={confirm}
              onChange={(event) => setConfirm(event.target.value)}
              required
              fullWidth
              autoComplete="new-password"
              error={mismatch}
            />
          </Field>
          <Button
            type="submit"
            variant="contained"
            size="large"
            fullWidth
            disabled={busy || mismatch || tooShort || password === "" || confirm === ""}
          >
            {busy ? "Saving…" : "Set password"}
          </Button>
        </Stack>
      </form>
      <Failure error={error} />
    </LandingLayout>
  );
}
